// dependencies
import { cva } from "class-variance-authority";
import React from "react";

import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";

import { faCircleCheck, faCircleExclamation, faCircleInfo } from "@performyard/fontawesome/pro-regular-svg-icons";
import {
	faCircleCheck as faCircleCheckSolid,
	faCircleExclamation as faCircleExclamationSolid,
	faCircleInfo as faCircleInfoSolid,
} from "@performyard/fontawesome/pro-solid-svg-icons";

import { cn } from "../../../lib/utils";

import { ColorShade } from "../../../theme";
// components
import { Icon, IconProps } from "../../atoms/Icon";
import { Text, TextProps } from "../../atoms/Text";

const wrapperVariants = cva("inline-flex gap-1", {
	defaultVariants: {
		items: "center",
	},
	variants: {
		items: {
			start: "items-start",
			center: "items-center",
			end: "items-end",
		},
	},
});

export interface AlertProps {
	/**
	 * The message to display in the alert
	 */
	message: string;
	/**
	 * The type of alert to display. Affects the color of the alert message.
	 */
	type: "error" | "success" | "neutral";
	/**
	 * The variant of the alert to display
	 */
	variant?: "regular" | "duotone";
	/**
	 * The icon to override the default icon
	 */
	icon?: IconDefinition;
	/**
	 * Optional class name to add to the alert
	 */
	dangerouslySetClassName?: string;
	/**
	 * Whether the alert should be announced to screen readers immediately
	 * @default true for error and success types, false for neutral
	 */
	live?: boolean;
	/**
	 * Custom aria-label for the alert. If not provided, will use the message.
	 */
	ariaLabel?: string;

	/**
	 * The font weight of the icon
	 * @default "regular"
	 */
	weight?: TextProps["weight"];

	size?: TextProps["size"];
	/**
	 * The items of the icon
	 * @default "start"
	 */
	items?: "start" | "center" | "end";
}

const iconsRegular = {
	error: faCircleExclamation,
	success: faCircleCheck,
	neutral: faCircleInfo,
};

const iconsDuoTone = {
	error: faCircleExclamationSolid,
	success: faCircleCheckSolid,
	neutral: faCircleInfoSolid,
};

const ICON_COLORS: {
	[variant: string]: {
		[type: string]: string;
	};
} = {
	regular: {
		error: "red-5",
		success: "green-5",
		neutral: "neutral-9",
	},
	duotone: {
		error: "red-5",
		success: "green-5",
		neutral: "primary-5",
	},
};

const TEXT_COLORS: {
	[variant: string]: {
		[type: string]: string;
	};
} = {
	regular: {
		error: "red-5",
		success: "green-5",
		neutral: "neutral-9",
	},
	duotone: {
		error: "neutral-9",
		success: "neutral-9",
		neutral: "neutral-9",
	},
};

const ICON_SIZES_MAP: Record<TextProps["size"], IconProps["size"]> = {
	"paragraph-3": 16,
	"paragraph-2": 20,
	"paragraph-1": 24,
	"sub-heading": 20,
	"heading-5": 24,
	"heading-4": 24,
	"heading-3": 24,
	"heading-2": 32,
	"heading-1": 32,
	caption: 12,
	"extra-small": 12,
} as const;

/**
 * The Alert component is used to display a message to the user. The alert includes an icon and a text message.
 *
 * Accessibility features:
 * - Uses `role="alert"` for important messages that should be announced immediately
 * - Uses `role="status"` for informational messages that don't require immediate attention
 * - Includes `aria-live` regions for dynamic content announcements
 * - Provides semantic meaning through proper ARIA attributes
 * - Supports custom aria-label for more descriptive screen reader announcements
 */
const Alert = (props: AlertProps) => {
	const {
		message,
		type,
		variant = "regular",
		icon,
		dangerouslySetClassName,
		live,
		ariaLabel,
		weight = "regular",
		size = "paragraph-3",
		items = "center",
	} = props;

	// Determine if this alert should be live (announced immediately)
	const shouldBeLive = live ?? (type === "error" || type === "success");

	// Choose appropriate role based on alert type and live setting
	const role = shouldBeLive ? "alert" : "status";

	// Determine aria-live value based on urgency
	const ariaLive = shouldBeLive ? "assertive" : "polite";

	return (
		<div
			role={role}
			aria-live={ariaLive}
			aria-label={ariaLabel || `${type} alert: ${message}`}
			className={cn(wrapperVariants({ items }), dangerouslySetClassName)}
		>
			<Icon
				color={ICON_COLORS[variant][type] as ColorShade}
				icon={icon || (variant === "regular" ? iconsRegular[type] : iconsDuoTone[type])}
				aria-hidden="true"
				size={ICON_SIZES_MAP[size]}
			/>
			<Text size={size} color={TEXT_COLORS[variant][type] as ColorShade} weight={weight}>
				{message}
			</Text>
		</div>
	);
};

Alert.displayName = "Alert";

export { Alert };
